import { useEffect, useRef } from 'react';

import { ambientHeidi } from '@heidi/plugin-core';

import type { VoiceConnectionStatus } from '../store/useVoiceAssistantStore';

export type AmbientVoiceBridgePorts = {
  open: (options?: { audioSeed?: Promise<ArrayBuffer> }) => void;
  connection: VoiceConnectionStatus;
  /** Talk to Heidi's recorder holds a mic stream, including a live call in `error`. */
  voiceMicLive: boolean;
  /** A visit transcription owns the mic, including ones native capture cannot see. */
  visitRecording: boolean;
  onTriggered?: () => void;
};

/**
 * Subscribes to the Ambient Heidi trigger and opens Talk to Heidi when the mic is free.
 * Pauses the native listener while Talk to Heidi or a visit transcription holds the mic.
 */
export function useAmbientVoiceBridge({
  open,
  connection,
  voiceMicLive,
  visitRecording,
  onTriggered,
}: AmbientVoiceBridgePorts): void {
  const micHeld =
    connection === 'connecting' ||
    connection === 'active' ||
    voiceMicLive ||
    visitRecording;
  const openRef = useRef(open);
  openRef.current = open;
  const micHeldRef = useRef(micHeld);
  micHeldRef.current = micHeld;
  const onTriggeredRef = useRef(onTriggered);
  onTriggeredRef.current = onTriggered;

  useEffect(() => {
    if (!ambientHeidi.isAvailable()) {
      return;
    }
    void ambientHeidi.setPaused(micHeld);
    return () => {
      void ambientHeidi.setPaused(false);
    };
  }, [micHeld]);

  useEffect(() => {
    if (!ambientHeidi.isAvailable()) {
      return;
    }
    let cancelled = false;
    let unlisten: (() => Promise<void>) | undefined;

    void ambientHeidi
      .onTrigger(({ triggerId }) => {
        if (micHeldRef.current) {
          void ambientHeidi.cancelUtterance(triggerId);
          return;
        }
        onTriggeredRef.current?.();
        const clip = ambientHeidi.takeUtterance(triggerId).then(
          (buffer) => buffer,
          () => new ArrayBuffer(0)
        );
        openRef.current({ audioSeed: clip });
      })
      .then((fn) => {
        if (cancelled) {
          void fn();
          return;
        }
        unlisten = fn;
      });

    return () => {
      cancelled = true;
      if (unlisten) {
        void unlisten();
      }
    };
  }, []);
}
